import React, { useState, useEffect, useMemo } from "react";
import { utilsGetLocalStorage, utilsUpdateLocalStorage } from "../../utis/utilsUpdateLocalStorage";

/**
 * Informational banner that educates editor users about cookie-dependent content
 *
 * Shows a friendly, dismissible note when the KDF contains video or embedded content
 * that requires cookie consent for viewers to see.
 *
 * This is purely informational - it doesn't block workflows or affect functionality
 */
const CookieInfoBanner = ({ elements = {}, docId }) => {
  const [isDismissed, setIsDismissed] = useState(false);

  // Storage key for remembering dismissed state (per document)
  const dismissalKey = `cookie-info-banner-dismissed-${docId}`;

  // Check if user has previously dismissed this banner for this document
  useEffect(() => {
    const dismissed = utilsGetLocalStorage(dismissalKey);
    if (dismissed === "true") {
      setIsDismissed(true);
    }
  }, [dismissalKey]);

  // Check if there are any cookie-dependent elements (Video or Web URL)
  const hasCookieDependentContent = useMemo(() => {
    if (!elements || Object.keys(elements).length === 0) {
      return false;
    }

    return Object.values(elements).some(element => {
      const elementType = element?.element_type_name?.toLowerCase();
      return elementType === 'video' || elementType === 'web url';
    });
  }, [elements]);

  const handleDismiss = () => {
    setIsDismissed(true);
    utilsUpdateLocalStorage(dismissalKey, "true");
  };

  // Don't show if dismissed or no cookie-dependent content
  if (isDismissed || !hasCookieDependentContent) {
    return null;
  }

  const bannerStyle = {
    backgroundColor: '#e3f2fd',
    border: '1px solid #2196f3',
    borderRadius: '4px',
    padding: '10px 16px',
    margin: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    fontSize: '13px',
    color: '#1565c0',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  };

  const iconStyle = {
    marginRight: '12px',
    flexShrink: 0,
  };

  const contentStyle = {
    flex: 1,
    lineHeight: '1.5',
  };

  const closeButtonStyle = {
    background: 'none',
    border: 'none',
    color: '#1565c0',
    fontSize: '18px',
    cursor: 'pointer',
    padding: '0 8px',
    marginLeft: '12px',
    opacity: 0.7,
    transition: 'opacity 0.2s',
  };

  const closeButtonHoverStyle = {
    opacity: 1,
  };

  return (
    <div style={bannerStyle} role="alert">
      <div style={iconStyle}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
            stroke="#2196f3"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 16V12"
            stroke="#2196f3"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M12 8H12.01"
            stroke="#2196f3"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div style={contentStyle}>
        <strong>Cookie consent tip:</strong> This KDF contains embedded or video content.
        Recipients will need to accept cookies to view this content when they open the KDF.
      </div>
      <button
        style={closeButtonStyle}
        onClick={handleDismiss}
        onMouseEnter={(e) => e.target.style.opacity = '1'}
        onMouseLeave={(e) => e.target.style.opacity = '0.7'}
        title="Dismiss this message"
        aria-label="Dismiss"
      >
        ×
      </button>
    </div>
  );
};

export default CookieInfoBanner;
